/**
 * App — the SPA root for the single-pane evaluator UI (Wave 2c).
 *
 * One full-page view of one idea: the trial-execution pipeline on this host. The
 * App owns the two read polls — `GET /runs` (the recency-sorted, finished-capped
 * run rollup) via {@link usePolling}, and `GET /host-lens` + `GET /status` (the
 * interleaved cross-run trial stream and the host overlay) via
 * {@link useHostLensPolling} — and hands their snapshots to {@link PipelineView}.
 * Both thread `If-None-Match`/`304` and retain the last good snapshot across a
 * transient failure, so the surface ages honestly instead of blanking.
 *
 * Routing is hash-based through {@link parseRoute}: `#/` is the Pipeline home (the
 * live host board) and `#/history` the finished tail. The former redundant "Runs"
 * tab — which rendered the same pane at run fidelity — is gone; run fidelity is
 * now reached via the in-pane rocker. Orthogonally, `#trial=<id>` overlays the
 * {@link TrialDrawer} on whichever view is active — a card click writes that hash
 * and the drawer reads it, independent of the view route.
 *
 * The nav is derived from {@link VIEWS}, a single extensible view table, so adding
 * a top-level surface (a sibling effort adds a `host` view) is one entry here plus
 * one parse branch in `board-route.ts` — no scattered per-route conditionals.
 *
 * Every feed is credentialed by the operator's oauth2-proxy session cookie, so
 * identity can fail for the whole page at once. {@link SessionGate} wraps the
 * shell for that: the App folds each feed's identity failure into one verdict and
 * the gate renders a signed-out or not-permitted screen INSTEAD of the board.
 * Rendering the board with empty lanes under a dead session is the specific lie
 * this guards against — an idle host and an expired session must never look alike.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppShell } from './AppShell'
import type { Fidelity } from './FidelityRocker'
import { HostView } from './HostView'
import { PipelineView } from './PipelineView'
import type { PipelineScope } from './PipelineView'
import { SessionGate } from './SessionGate'
import type { SidebarItem } from './Sidebar'
import { SystemPill } from './SystemPill'
import { TrialDrawer } from './TrialDrawer'
import { cancelRun, fetchHostLens, fetchHostStatus } from './board-client'
import { type Route, hashForNav, parseRoute } from './board-route'
import { fetchRunsTagged, sessionFailureOf } from './client'
import { freshness } from './format'
import { type MetricsSnapshot, metricsHeadline } from './metrics'
import { METRICS_ENDPOINT, fetchMetricsTagged } from './metrics-client'
import { olderSuccessAt } from './pipeline-data'
import type { TaggedFetcher } from './polling'
import { mergeSessionFailure } from './session'
import { clearTrialSelection, readTrialFromLocation, selectTrial } from './trial-link'
import type { RunsSnapshot } from './types'
import { type HostLensPollingDeps, useHostLensPolling } from './useHostLensPolling'
import { usePolling } from './usePolling'

/**
 * The view table — the single source of truth for the sidebar nav and each
 * view's PipelineView configuration. One entry per top-level surface; its `id`
 * matches a {@link Route} kind one-to-one. Adding a view is one row here (and one
 * parse branch in `board-route.ts`), which is what keeps the nav extensible for
 * the sibling `host` view landing on top of this structure.
 */
interface PipelineViewConfig {
  /** Renders the PipelineView (the trial/run board). */
  surface: 'pipeline'
  id: Route['kind']
  /** Sidebar label and PipelineView toolbar title. */
  label: string
  /** Landing fidelity for the view — the rocker takes over locally after mount. */
  initialFidelity: Fidelity
  /** 'finished' restricts the view to terminal runs / done trials (History). */
  scope: PipelineScope
}

interface HostViewConfig {
  /** Renders the HostView (the host load/health surface). */
  surface: 'host'
  id: Route['kind']
  /** Sidebar label. */
  label: string
}

/**
 * A registered top-level surface. The two surface kinds keep the table
 * extensible WITHOUT forcing every view through the PipelineView mold: a
 * `pipeline` view carries the board's fidelity/scope config, a `host` view
 * carries none — it renders its own component. Adding either is one row here
 * plus one parse branch in `board-route.ts`.
 */
type ViewConfig = PipelineViewConfig | HostViewConfig

const VIEWS: readonly ViewConfig[] = [
  { surface: 'pipeline', id: 'pipeline', label: 'Pipeline', initialFidelity: 'trial', scope: 'all' },
  { surface: 'pipeline', id: 'history', label: 'History', initialFidelity: 'run', scope: 'finished' },
  { surface: 'host', id: 'host', label: 'Host' },
]

const NAV: SidebarItem[] = VIEWS.map(({ id, label }) => ({ id, label }))

const VIEW_BY_KIND: Record<Route['kind'], ViewConfig> = Object.fromEntries(
  VIEWS.map((view) => [view.id, view])
) as Record<Route['kind'], ViewConfig>

/** Subscribe to `location.hash` and return the parsed current route. */
function useHashRoute(): Route {
  const read = useCallback((): Route => parseRoute(globalThis.location?.hash ?? ''), [])
  const [route, setRoute] = useState<Route>(read)
  useEffect(() => {
    const onChange = (): void => {
      // `#trial=<id>` is the drawer-overlay hash — it does not represent a view
      // change. Ignore it here so the active view stays mounted beneath the
      // drawer. `useTrialSelection` handles that hash independently.
      const hash = globalThis.location?.hash ?? ''
      if (!hash.startsWith('#trial=')) {
        setRoute(read())
      }
    }
    globalThis.addEventListener?.('hashchange', onChange)
    return () => globalThis.removeEventListener?.('hashchange', onChange)
  }, [read])
  return route
}

/**
 * Track the trial selected for the drawer via the URL hash (`#trial=<id>`). The
 * hash is the single source of truth so a card click, a deep link, and the
 * browser Back button all converge on one value. This is orthogonal to the view
 * route: the drawer overlays whichever view is active. Returns the current
 * selection and a closer that clears it.
 */
function useTrialSelection(): { trialId: string | null; close: () => void } {
  const [trialId, setTrialId] = useState<string | null>(() => readTrialFromLocation())
  useEffect(() => {
    const sync = (): void => setTrialId(readTrialFromLocation())
    globalThis.addEventListener?.('hashchange', sync)
    return () => globalThis.removeEventListener?.('hashchange', sync)
  }, [])
  const close = useCallback(() => {
    clearTrialSelection()
    setTrialId(null)
  }, [])
  return { trialId, close }
}

export function App({ fetchImpl, intervalMs }: { fetchImpl?: typeof fetch; intervalMs?: number } = {}): JSX.Element {
  const route = useHashRoute()
  const view = VIEW_BY_KIND[route.kind]
  const { trialId, close } = useTrialSelection()

  // The run rollup poll (`GET /runs`). Stable for the lifetime of a given
  // fetchImpl so the poll effect does not re-arm on every render.
  const runsFetcher = useMemo<TaggedFetcher<RunsSnapshot>>(
    () => (etag) => fetchRunsTagged(etag, fetchImpl),
    [fetchImpl]
  )
  // Bumped after a run cancel to force an immediate `/runs` re-fetch (on top of
  // the relaxed interval) so the just-cancelled run leaves the live lanes
  // promptly instead of lingering until the next tick.
  const [runsRefreshToken, setRunsRefreshToken] = useState(0)
  const refetchRuns = useCallback(() => setRunsRefreshToken((n) => n + 1), [])
  const runsPoll = usePolling(runsFetcher, {
    ...(intervalMs === undefined ? {} : { intervalMs }),
    refreshToken: runsRefreshToken,
  })

  // Bind the App's fetchImpl around the client cancel so the run rows can fire
  // `POST /runs/<id>/cancel`. The operator's session cookie is what authorizes
  // it now — the edge no longer injects the daemon's trigger secret on this path.
  const onCancelRun = useCallback((runId: string) => cancelRun(runId, { fetchImpl }), [fetchImpl])

  // The interleaved trial stream + host overlay poll (`GET /host-lens`, `/status`).
  const lensDeps = useMemo<HostLensPollingDeps>(
    () => ({
      fetchLensImpl: (options) => fetchHostLens({ ...options, fetchImpl }),
      fetchHostImpl: () => fetchHostStatus({ fetchImpl }),
      ...(intervalMs === undefined ? {} : { intervalMs }),
    }),
    [fetchImpl, intervalMs]
  )
  const lensPoll = useHostLensPolling(lensDeps)

  // The host metrics poll (`GET /metrics`) — drives the topbar CPU/mem headline
  // and the Host view's time-series + health panel. Stable per fetchImpl so the
  // poll effect does not re-arm each render.
  const metricsFetcher = useMemo<TaggedFetcher<MetricsSnapshot>>(
    () => (etag) => fetchMetricsTagged(etag, fetchImpl),
    [fetchImpl]
  )
  const metricsPoll = usePolling(metricsFetcher, {
    ...(intervalMs === undefined ? {} : { intervalMs }),
    fallbackEndpoint: METRICS_ENDPOINT,
  })
  const metricsError = metricsPoll.error
    ? { endpoint: metricsPoll.error.endpoint, message: metricsPoll.error.message }
    : null
  const headline = metricsHeadline(metricsPoll.data)

  const runsError = runsPoll.error ? { endpoint: runsPoll.error.endpoint, message: runsPoll.error.message } : null

  // Identity, folded across all four feeds. Any one of them saying "who are you"
  // is the whole page's answer — a board with three live panels and one
  // silently-401ing panel is exactly the half-truth this release removes.
  // `mergeSessionFailure` gives 403 precedence so a stale 401 from a slower feed
  // cannot downgrade a terminal not-permitted into a redirect loop.
  const sessionFailure = [
    sessionFailureOf(runsPoll.error),
    lensPoll.sessionFailure,
    sessionFailureOf(metricsPoll.error),
  ].reduce(mergeSessionFailure, null)

  // Whether anything real ever rendered. It decides auto-redirect vs. a visible
  // expired-session state, so it must mean "data arrived", not "a poll resolved".
  const everLoaded = runsPoll.lastSuccessAt !== null || lensPoll.lastSuccessAt !== null

  // The topbar pill reflects the freshness of BOTH feeds, not just `/runs`. The
  // visible Pipeline stream is backed by `/host-lens`, so a `/host-lens` failure
  // must age the pill even while `/runs` keeps succeeding: take the stalest
  // last-success and treat either feed failing as failing.
  const fresh = freshness(
    olderSuccessAt(runsPoll.lastSuccessAt, lensPoll.lastSuccessAt),
    runsPoll.failing || lensPoll.error !== null
  )

  const onNav = useCallback((id: string) => {
    if (globalThis.location) {
      globalThis.location.hash = hashForNav(id as Route['kind'])
    }
  }, [])

  return (
    <SessionGate failure={sessionFailure} everLoaded={everLoaded}>
      <AppShell
        nav={NAV}
        active={route.kind}
        onNav={onNav}
        systemPill={<SystemPill host={lensPoll.host} headline={headline} fresh={fresh} />}
      >
        {view.surface === 'host' ? (
          <HostView metrics={metricsPoll.data} error={metricsError} />
        ) : (
          <PipelineView
            key={view.id}
            title={view.label}
            runs={runsPoll.data}
            lens={lensPoll.snapshot}
            runsError={runsError}
            lensError={lensPoll.error}
            initialFidelity={view.initialFidelity}
            scope={view.scope}
            onSelectTrial={selectTrial}
            onCancelRun={onCancelRun}
            onRunCancelled={refetchRuns}
          />
        )}
      </AppShell>
      <TrialDrawer trialId={trialId} onClose={close} fetchImpl={fetchImpl} />
    </SessionGate>
  )
}
